import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { SalesInvoiceRow } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { PageHeader } from '@/components/PageHeader';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { SectionCard } from '@/components/SectionCard';
import { ErrorState } from '@/components/States';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { TablePager } from '@/components/TablePager';
import { KpiGrid, KpiTile, KpiTileSkeleton } from '@/features/portal/KpiTile';
import { ExportMenu } from '@/features/portal/ExportMenu';
import { usePortal } from '@/features/portal/PortalContext';
import { fetchSalesReport, portalKeys } from '@/features/portal/api';
import { toReportRange } from '@/features/portal/filters';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { formatCount, formatMoney, formatPosDate, formatPosTime, parseAmount } from '@/lib/format';

/** Orders per page. A busy month then reads as several short pages rather than one long one. */
const PAGE_SIZE = 15;

const FILTER_FIELDS = ['Order Number', 'Customer'] as const;

const TOGGLEABLE = [
  'time', 'items', 'cash', 'card', 'credit', 'due', 'gross', 'taxPercent', 'taxable', 'tax', 'returns', 'net',
] as const;

const COLUMN_LABELS: Record<string, string> = {
  time: 'Time',
  items: 'Items',
  cash: 'Cash',
  card: 'Card',
  credit: 'Credit',
  due: 'Due',
  gross: 'Gross Sales',
  taxPercent: 'Tax %',
  taxable: 'Taxable Amount',
  tax: 'VAT',
  returns: 'Returns',
  net: 'Net Sales',
};

/**
 * The Sales Report table, row for row with the POS's own Sales Report so an
 * owner can hold the two up against each other. One row is one ORDER, not a
 * day rollup — a busy date repeats across several rows, same as the till.
 *
 * `net` is the till's definition (gross − VAT − returns), which is why it does
 * not equal the dashboard's Total sales (gross − returns).
 */
function invoiceColumns(currency: string): Array<Column<SalesInvoiceRow>> {
  const money = (value: string) => formatMoney(value, currency);
  // A zero in a money column is noise on a report this wide; the POS prints a
  // dash for the empty ones and this follows it.
  const dashIfZero = (value: string) =>
    parseAmount(value) === 0 ? <span className="text-slate-300">—</span> : money(value);

  return [
    {
      key: 'date',
      header: 'Date',
      pinned: true,
      cell: (row) => (
        <span className="whitespace-nowrap font-medium text-slate-900">
          {formatPosDate(row.date)}
        </span>
      ),
    },
    {
      key: 'orderNumber',
      header: 'Order Number',
      pinned: true,
      cell: (row) => <span className="whitespace-nowrap text-slate-700">{row.orderNumber}</span>,
    },
    {
      key: 'customerName',
      header: 'Customer',
      pinned: true,
      cell: (row) => <span className="text-slate-700">{row.customerName}</span>,
    },
    {
      // When the order was placed, as Order History shows it. A per-order
      // fact, so the grand-total row leaves it blank.
      key: 'time',
      header: 'Time',
      cell: (row) =>
        row.time ? <span className="whitespace-nowrap text-slate-700">{formatPosTime(row.time)}</span> : '',
    },
    {
      key: 'items',
      header: 'Items',
      align: 'right',
      visibility: 'hidden sm:table-cell',
      cell: (row) => formatCount(row.items),
    },
    { key: 'cash', header: 'Cash', align: 'right', cell: (row) => dashIfZero(row.cash) },
    { key: 'card', header: 'Card', align: 'right', cell: (row) => dashIfZero(row.card) },
    { key: 'credit', header: 'Credit', align: 'right', cell: (row) => dashIfZero(row.credit) },
    {
      key: 'due',
      header: 'Due',
      align: 'right',
      // Unpaid with no method yet — postpaid delivery that has not settled.
      //
      // The one column that gives way on a narrow screen. Due is the cheapest
      // to lose: it is zero unless the shop runs postpaid delivery, and it is
      // still reachable on a wide screen and inside the horizontal scroll.
      visibility: 'hidden 2xl:table-cell',
      cell: (row) => dashIfZero(row.due),
    },
    {
      key: 'gross',
      header: 'Gross Sales',
      align: 'right',
      cell: (row) => <span className="font-medium text-slate-900">{money(row.gross)}</span>,
    },
    {
      key: 'taxPercent',
      header: 'Tax %',
      align: 'right',
      visibility: 'hidden lg:table-cell',
      cell: (row) => <span className="text-slate-700">{row.taxPercent}%</span>,
    },
    {
      key: 'taxable',
      header: 'Taxable Amount',
      align: 'right',
      visibility: 'hidden xl:table-cell',
      cell: (row) => dashIfZero(row.taxable),
    },
    { key: 'tax', header: 'VAT', align: 'right', cell: (row) => dashIfZero(row.tax) },
    {
      key: 'returns',
      header: 'Returns',
      align: 'right',
      cell: (row) =>
        parseAmount(row.returns) === 0 ? (
          <span className="text-slate-300">—</span>
        ) : (
          <span className="text-rose-700">{money(row.returns)}</span>
        ),
    },
    {
      key: 'net',
      header: 'Net Sales',
      align: 'right',
      cell: (row) => <span className="font-semibold text-slate-900">{money(row.net)}</span>,
    },
  ];
}

/**
 * Sales Report: the summary figures and the per-order table, and nothing else.
 *
 * This page deliberately carries NO charts. It used to open with a trend line, a
 * payment-method donut and three bar lists, all of which the Dashboard already
 * shows — so arriving here looked like landing back on the Dashboard, and the
 * report itself sat below the fold. Charts live on the Dashboard; this is the
 * page you read beside the till's printed report.
 *
 * Paging is done in the browser, not the API. The date range is capped at 400
 * days, so the whole report is at most a few hundred rows in one response, and
 * the grand total has to span the entire range rather than the visible page —
 * fetching page by page would either cost a round trip per page or make that
 * total ambiguous.
 */
export function SalesPage() {
  const { currency, filters } = usePortal();
  const [searchParams, setSearchParams] = useSearchParams();
  const range = useMemo(() => toReportRange(filters.filters), [filters.filters]);
  const toolbar = useReportToolbar();
  const layout = useReportLayout('sales-report', TOGGLEABLE);

  const salesQuery = useQuery({
    queryKey: portalKeys.sales(range),
    queryFn: ({ signal }) => fetchSalesReport(range, signal),
  });

  const report = salesQuery.data;
  const reportCurrency = report?.currency || currency;
  const columns = useMemo(
    () => applyReportLayout(invoiceColumns(reportCurrency), layout),
    [reportCurrency, layout],
  );

  // The whole result set is already in the browser (this report isn't
  // server-paginated), so the toolbar's search + Filters are applied here —
  // the client-side twin of what Orders does in its API query.
  const invoices = useMemo(
    () =>
      applyClientFilters(report?.invoices ?? [], {
        search: toolbar.search,
        filterRows: toolbar.filterRows,
        matchAny: toolbar.matchAny,
        fieldValue: (row, field) =>
          field === 'Order Number' ? row.orderNumber : field === 'Customer' ? row.customerName : undefined,
        searchValues: (row) => [row.orderNumber, row.customerName],
      }),
    [report, toolbar.search, toolbar.filterRows, toolbar.matchAny],
  );
  const totalPages = Math.max(1, Math.ceil(invoices.length / PAGE_SIZE));
  // Clamped rather than trusted: changing the date range can leave a `page=9`
  // from the previous, longer range in the URL, and an out-of-range page would
  // render an empty table over a populated report.
  const rawPage = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const page = Number.isFinite(rawPage) ? Math.min(Math.max(1, rawPage), totalPages) : 1;

  const setPage = useCallback(
    (next: number) => {
      const params = new URLSearchParams(searchParams);
      // Page 1 is the default; keeping it out of the URL stops every shared link
      // carrying a redundant `page=1`.
      if (next <= 1) params.delete('page');
      else params.set('page', String(next));
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const visible = useMemo(
    () => invoices.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [invoices, page],
  );

  // The total spans the WHOLE range, not this page — said out loud in the label,
  // because a totals row under a paginated table otherwise reads as the page's.
  const invoiceTotals = useMemo(() => {
    const total = report?.invoiceTotal;
    if (!total) return undefined;
    const money = (value: string) => formatMoney(value, reportCurrency);
    const orders = report?.invoices.length ?? 0;
    return {
      label: totalPages > 1 ? `Grand total · all ${orders} orders` : 'Grand total',
      cells: {
        items: formatCount(total.items),
        cash: money(total.cash),
        card: money(total.card),
        credit: money(total.credit),
        due: money(total.due),
        gross: money(total.gross),
        taxable: money(total.taxable),
        tax: money(total.tax),
        returns: money(total.returns),
        net: money(total.net),
      },
    };
  }, [report, reportCurrency, totalPages]);

  const header = (
    <PageHeader
      title="Sales report"
      description="One row per order for the selected range, branch and till — the same figures the till prints."
      actions={
        <ExportMenu
          report="sales"
          range={range}
          // Nothing to export until the report has loaded, and an
          // errored report would export an empty file.
          disabled={salesQuery.isPending || salesQuery.isError}
        />
      }
    />
  );

  if (salesQuery.isError) {
    return (
      <>
        {header}
        <div className="card">
          <ErrorState
            error={salesQuery.error}
            onRetry={() => void salesQuery.refetch()}
            retrying={salesQuery.isFetching}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {header}

      <div className="space-y-6">
        <AsyncSection
          state={salesQuery}
          skeleton={
            <KpiGrid>
              {Array.from({ length: 4 }).map((_, index) => (
                <KpiTileSkeleton key={index} />
              ))}
            </KpiGrid>
          }
        >
          {(data) => (
            <KpiGrid>
              <KpiTile
                label="Net sales"
                value={formatMoney(data.totals.totalSales, data.currency || currency)}
                hint={`Gross ${formatMoney(data.totals.grossSales, data.currency || currency)}`}
              />
              <KpiTile label="Orders" value={formatCount(data.totals.orderCount)} />
              <KpiTile
                label="Average order"
                value={formatMoney(data.totals.averageOrderValue, data.currency || currency)}
              />
              <KpiTile
                label="Returns"
                value={formatMoney(data.totals.returnsAmount, data.currency || currency)}
                tone="negative"
                hint={`Tax ${formatMoney(data.totals.taxCollected, data.currency || currency)}`}
              />
            </KpiGrid>
          )}
        </AsyncSection>

        <SectionCard
          headingId="sales-report"
          title="Order breakdown"
          count={invoices.length}
          description="Same columns as the till's own Sales Report. Scroll sideways for the full breakdown."
        >
          <ReportToolbar>
            <ReportSearchField
              value={toolbar.search}
              onChange={toolbar.setSearch}
              placeholder="Order number or customer"
            />
            <ReportFiltersButton
              fields={FILTER_FIELDS}
              applied={toolbar.filterRows}
              matchAny={toolbar.matchAny}
              onApply={toolbar.applyFilters}
            />
            <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
          </ReportToolbar>
          <AsyncSection
            state={salesQuery}
            skeleton={<TableSkeleton rows={6} columns={8} />}
            isEmpty={() => invoices.length === 0}
            emptyTitle={toolbar.search ? `No orders match “${toolbar.search}”` : 'No sales in this range'}
            emptyDescription="Pick a wider date range, or check another branch or till."
          >
            {() => (
              <DataTable
                caption="One row per order — date, customer, items, payment split, tax, returns and net sales"
                columns={columns}
                rows={visible}
                rowKey={(row) => `${row.date}-${row.orderNumber}`}
                totals={invoiceTotals}
                layout={layout}
              />
            )}
          </AsyncSection>

          {!salesQuery.isPending && !salesQuery.isError ? (
            <TablePager
              page={page}
              pageSize={PAGE_SIZE}
              total={invoices.length}
              onPageChange={setPage}
              busy={salesQuery.isFetching}
              noun="order"
            />
          ) : null}
        </SectionCard>
      </div>
    </>
  );
}
